import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Sidebar from './Sidebar'; // Import the Sidebar component
import { Line } from 'react-chartjs-2'; // Import Chart component from react-chartjs-2
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register components for Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DailySalesWrapper = styled.div`
  display: flex;
  height: 100vh; /* Ensures full viewport height */
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  background-color: #f5f5f5;
  overflow-y: auto; /* Allows scrolling if content exceeds available space */
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin: 20px 0;
`;

const TableWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f1f1f1;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }
`;

const TableCell = styled.td`
  padding: 10px;
  border: 1px solid #ddd;
`;

const ChartContainer = styled.div`
  margin-top: 20px;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  height: 400px; /* Ensure chart height fits well */
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DailySales = () => {
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    country: 'All',
    nip: '',
    contractorName: ''
  });

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  // Fetch data from the backend and skip records with null orderNo
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('https://server229256.nazwa.pl/fetch-external-data');
        const validRecords = response.data.data
          .filter(item => item?.orderNo) // Use optional chaining to avoid null checks
          .slice(0, 50); // Limit to first 50 records
        setRecords(validRecords);

        // Prepare data for the chart
        const chartLabels = [];
        const chartValues = [];

        validRecords.forEach(record => {
          const brutto = (record.k29 || 0) + (record.k30 || 0);
          chartLabels.push(record.contractorName || 'N/A');
          chartValues.push(brutto);
        });

        setChartData({
          labels: chartLabels,
          datasets: [
            {
              label: 'Brutto (Lordo) Values',
              data: chartValues,
              borderColor: 'rgba(75, 192, 192, 1)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.4,
              fill: true
            }
          ]
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Update filter state
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  // Apply filters based on selected criteria
  const filteredRecords = records.filter((record) => {
    return (
      (!filters.dateFrom || new Date(record.documentIssueDate) >= new Date(filters.dateFrom)) &&
      (!filters.dateTo || new Date(record.documentIssueDate) <= new Date(filters.dateTo)) &&
      (filters.country === 'All' || record.contractorCountry === filters.country) &&
      (!filters.nip || record.contractorNo?.includes(filters.nip)) &&
      (!filters.contractorName || record.contractorName?.toLowerCase().includes(filters.contractorName.toLowerCase()))
    );
  });

  return (
    <DailySalesWrapper>
      <Sidebar />

      <Content>
        {/* Filters Section */}
        <Card>
          <h2>Filters</h2>
          <div>
            <label>Date From:</label>
            <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
            <label>Date To:</label>
            <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
            <label>Country:</label>
            <select name="country" value={filters.country} onChange={handleFilterChange}>
              <option value="All">All</option>
              <option value="PL">PL</option>
              <option value="DE">DE</option>
            </select>
            <label>NIP:</label>
            <input type="text" name="nip" value={filters.nip} onChange={handleFilterChange} />
            <label>Contractor Name:</label>
            <input type="text" name="contractorName" value={filters.contractorName} onChange={handleFilterChange} />
          </div>
        </Card>

        {/* Records Table */}
        <Card>
          <h2>Daily Sales Records</h2>
          <TableWrapper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>NIP</TableCell>
                  <TableCell>Nazwa Firmy</TableCell>
                  <TableCell>Brutto (Lordo)</TableCell>
                  <TableCell>Netto</TableCell>
                </TableRow>
              </TableHead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell>{record.contractorNo || 'N/A'}</TableCell>
                      <TableCell>{record.contractorName || 'N/A'}</TableCell>
                      <TableCell>{(record.k29 || 0) + (record.k30 || 0)}</TableCell>
                      <TableCell>{record.k29 || 0}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan="5">No records found</TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>

        {/* Chart Component */}
        <ChartContainer>
          {chartData.labels.length > 0 ? (
            <Line data={chartData} options={{ maintainAspectRatio: false }} />
          ) : (
            <p>No chart data available</p>
          )}
        </ChartContainer>
      </Content>
    </DailySalesWrapper>
  );
};

export default DailySales;
